import {
  AfterViewInit,
  ChangeDetectorRef,
  Component, ElementRef, HostBinding, HostListener, inject, NgZone, OnDestroy,
  OnInit, Renderer2, ViewChild,
} from '@angular/core';
import { NotificationService } from '../../core/services';
import { GenericService } from '../../core/services/GenericService';
import { ITableResults } from '../../core/interfaces';
import { NzTableComponent, NzTableQueryParams } from 'ng-zorro-antd/table';
import { environment } from '../../../environments/environment';
import { Subscription } from 'rxjs';
import { KeepStateDirective } from '../../core/directives';
import { BaseServiceErrorResolverService } from '../../core/services/error-resolvers/base-service-error-resolver.service';
import { ExportTableModalComponent } from '../modals/export-table-modal/export-table-modal.component';
import { IExportTable } from '../../pages/bulk-containers/core/interfaces/IExportTable';
import { finalize } from 'rxjs/operators';
import { FilterOperator } from '../../core/types';
import {
  stampTableCardLabels,
  TABLE_FLUID_COL_MIN_PX,
  TABLE_MOBILE_MAX_PX,
  TABLE_ROW_ESTIMATE_PX,
  tableScrollForViewport,
  COLUMN_LAYOUT_EVENT,
  COLUMN_HIDDEN_CLASS,
  COLUMN_USER_WIDTH_ATTR,
  measureColumnHeaderContentWidth,
} from '../../core/utils';

@Component({
    template: '',
    standalone: false
})
export abstract class ListComponentGeneric<T> implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('tableRef') tableRef: NzTableComponent<T>;
  @ViewChild('exportTableModalComponent') exportTableModalComponent: ExportTableModalComponent;
  @ViewChild(KeepStateDirective, {static: true}) keepStateDirective: KeepStateDirective;

  public data: ITableResults<T> = {
    results: [],
    info: {
      page: 1,
      results: 0,
      totalResults: 0,
      exportTableName: null
    }
  };
  protected subscriptions: Subscription[] = [];
  @HostBinding('style.display') readonly hostDisplay = 'block';
  @HostBinding('style.width') readonly hostWidth = '100%';
  @HostBinding('style.min-width') readonly hostMinWidth = '0';
  @HostBinding('class.list-is-loading')
  public loading = true;
  @HostBinding('class.list-has-error')
  public listError = false;
  public pageSize = environment.defaultResultsPerPage;
  public checked = false;
  public indeterminate = false;
  public setOfCheckedId = new Set<number>();
  protected searchColumns: string[]|null = null;
  public tableMaxWidth: string;
  public readonly virtualItemSize = 72;
  public useVirtualScroll = false;
  public tableScroll: { x: string; y?: string } = { x: '100%' };
  public currentSearch = '';
  protected pageWrapperEl: HTMLElement;
  protected predefindedFilters: { key: string; value: string[]; operator?: FilterOperator; }[] = [];
  protected exportProperties: {[key: string]: any } = {};
  protected readonly abstract exportedTableVisibleName: string;
  protected readonly defaultSorting: { sortField: string; sortOrder: string; };
  protected lastLoadParams: {
    pageIndex: number,
    pageSize: number,
    sortField: string | null,
    sortOrder: string | null,
    filter: { key: string; value: string[] }[],
    search: { key: string; value: string[] }[],
    additionalFilters: { key: string; value: string[] }[]
  } = {
    pageIndex: null,
    pageSize: 10,
    sortField: null,
    sortOrder: null,
    filter: [],
    search: [],
    additionalFilters: []
  };
  protected readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  protected readonly renderer2 = inject(Renderer2);
  private readonly ngZone = inject(NgZone);
  private tableWidthObserver: ResizeObserver | null = null;
  private tableWidthRaf = 0;
  private tableWidthWait = 0;
  private onColumnLayout = (): void => this._setTableMaxWidth();

  protected constructor(
    protected _listService: GenericService<T>,
    protected _cdr: ChangeDetectorRef,
    protected _notifyService: NotificationService,
    protected renderer: Renderer2,
    protected _errorResolver: BaseServiceErrorResolverService,
  ) { }

  @HostListener('window:resize')
  onResize() {
    this._setTableMaxWidth();
  }

  /**
   * BAO-983: pin the pager in the viewport when the page of rows would push it below the fold.
   * Virtual scroll still only above 20 / Seite so a 10-row page on a large screen has no inner y-bar.
   */
  protected updateTableScroll(): void {
    const pageSize = Math.max(this.lastLoadParams.pageSize || this.pageSize || 10, 1);
    const x = this.tableMaxWidth || '100%';
    this.useVirtualScroll = pageSize > 20;
    const tableEl = this.elementRef?.nativeElement?.querySelector('nz-table') ?? null;
    const rowCount = this.data?.results?.length || pageSize;
    const next = tableScrollForViewport(x, {
      tableEl,
      rowCount,
      rowHeight: this.useVirtualScroll ? this.virtualItemSize : TABLE_ROW_ESTIMATE_PX,
      forceY: this.useVirtualScroll,
    });
    if (next.x !== this.tableScroll?.x || next.y !== this.tableScroll?.y) {
      this.tableScroll = next;
    }
  }

  ngOnInit(): void {
    this.restorePersistedTablePrefs();
  }

  /** BAO-971: never restore search/page/filters. Silent subset looks like the full list. */
  protected restorePersistedTablePrefs(): void {
    const stored = this.keepStateDirective?.getValue() as { pageSize?: number } | string | undefined;
    if (!stored || stored === '') {
      return;
    }
    const pageSize = typeof stored === 'object' ? stored.pageSize : undefined;
    if (typeof pageSize === 'number' && pageSize > 0) {
      this.pageSize = pageSize;
      this.lastLoadParams.pageSize = pageSize;
      this.updateTableScroll();
    }
  }

  protected persistTablePrefs(): void {
    if (!this.keepStateDirective) {
      return;
    }
    this.keepStateDirective.item = {
      pageSize: this.lastLoadParams.pageSize,
    };
  }

  ngAfterViewInit(): void {
    this.pageWrapperEl = this.renderer2.selectRootElement('.ant-layout.nz-layout-wrapper', true);
    this._observeTableWidthBox();
    this._setTableMaxWidth();

    if (window.innerWidth < TABLE_MOBILE_MAX_PX) {
      this.tableRef.nzPaginationType = 'small';
    }

    this.tableRef.nzCurrentPageDataChange.subscribe(() => {
      if (!this.pageWrapperEl.classList.contains('is-dashboard')) {
        this.pageWrapperEl.scrollTo(0, 0);
      }

      setTimeout(() => {
        stampTableCardLabels(this.elementRef.nativeElement);
        this._setTableMaxWidth();
      });
    });
    this.elementRef.nativeElement.addEventListener(COLUMN_LAYOUT_EVENT, this.onColumnLayout);
  }

  /** `.inner-content` follows the viewport; `nz-table` does not (scroll.x min-width). */
  private _tableWidthBox(): HTMLElement | null {
    return (this.elementRef.nativeElement.closest('.inner-content') as HTMLElement)
      || this.elementRef.nativeElement.parentElement
      || this.elementRef.nativeElement;
  }

  private _observeTableWidthBox(): void {
    const box = this._tableWidthBox();
    if (!box || typeof ResizeObserver === 'undefined') {
      return;
    }
    this.tableWidthObserver?.disconnect();
    this.tableWidthObserver = new ResizeObserver(() => this._setTableMaxWidth());
    this.tableWidthObserver.observe(box);
  }

  /**
   * scroll.x is the sum of header-text widths. Do not write those widths back
   * onto `col` — ng-zorro measures the columns itself, and a second writer makes
   * the swapped pair jump every frame.
   */
  protected _setTableMaxWidth(): void {
    if (this.tableWidthRaf) {
      cancelAnimationFrame(this.tableWidthRaf);
    }
    this.tableWidthRaf = requestAnimationFrame(() => {
      this.tableWidthRaf = 0;
      this.ngZone.run(() => this._applyTableMaxWidth());
    });
  }

  private _applyTableMaxWidth(): void {
    const tableEl = this.elementRef.nativeElement.querySelector('nz-table') as HTMLElement | null;
    if (!tableEl) {
      return;
    }
    const headerCells = this._ownHeaderCells(tableEl);
    if (!headerCells.length) {
      if (this.tableWidthWait < 10) {
        this.tableWidthWait += 1;
        this._setTableMaxWidth();
      }
      return;
    }
    this.tableWidthWait = 0;
    if (window.innerWidth < TABLE_MOBILE_MAX_PX) {
      this._applyMobileTableWidth();
      return;
    }
    const minContent = headerCells.reduce((sum, th) => sum + this._columnWidth(th), 0);
    if (minContent <= 0) {
      return;
    }
    const next = `${Math.round(minContent)}px`;
    if (next === this.tableMaxWidth) {
      return;
    }
    this.tableMaxWidth = next;
    this.updateTableScroll();
    this._cdr.detectChanges();
  }

  /** Header label width. A squeezed cell box is not a width. */
  private _columnWidth(th: HTMLElement): number {
    if (th.classList.contains(COLUMN_HIDDEN_CLASS)) {
      return 0;
    }
    const userWidth = parseFloat(th.getAttribute(COLUMN_USER_WIDTH_ATTR) || '');
    if (Number.isFinite(userWidth) && userWidth > 0) {
      return Math.round(userWidth);
    }
    const declared = parseFloat(th.getAttribute('nzwidth') || '');
    const headerContent = measureColumnHeaderContentWidth(th);
    const width = Math.max(
      Number.isFinite(declared) && declared > 0 ? declared : 0,
      headerContent,
      TABLE_FLUID_COL_MIN_PX,
    );
    return Math.round(width);
  }

  private _ownHeaderCells(tableEl: HTMLElement): HTMLElement[] {
    const rows = Array.from(tableEl.querySelectorAll<HTMLTableRowElement>('.ant-table-thead tr'))
      .filter(row => row.closest('nz-table') === tableEl && row.children.length > 0);
    const leaf = rows[rows.length - 1];
    return leaf ? Array.from(leaf.children) as HTMLElement[] : [];
  }

  /** Stacked cards on `$s`: table is 100% wide, no pixel min-content scroll. */
  private _applyMobileTableWidth(): void {
    const next = '100%';
    if (next !== this.tableMaxWidth) {
      this.tableMaxWidth = next;
      this.updateTableScroll();
      this._cdr.detectChanges();
    }
  }

  onRefreshList(defValues = true): void {
    if (defValues) {
      this.loadDefault();
      return;
    }
    const { pageIndex, pageSize, filter, sortField, sortOrder, search } = this.lastLoadParams;
    this.load(pageIndex, pageSize, sortField, sortOrder, filter, search);
    this._cdr.detectChanges();
  }

  public exportBtnClicked(): void {
    const { sortField, sortOrder, filter, additionalFilters } = this.lastLoadParams;
    const tableRef = this.tableRef as any;
    const tableParams: IExportTable = {
      visibleName: this.exportedTableVisibleName,
      fields: Array.from(tableRef.elementRef.nativeElement.querySelectorAll('th')).map((el: HTMLElement) => {
        return {
          label: el.textContent,
          value: el.getAttribute('nzcolumnkey') || el.getAttribute('data-nzColumnKey')
        };
      }).filter(({ label, value }) => label !== '' && value),
      search: this.assignSearch(this.currentSearch).map(({ key, value }) => `${ key }:${ value }`),
      sortField,
      sortOrder,
      filter,
      exportTableName: this.data.info.exportTableName,
      properties: this.exportProperties,
      additionalFilters
    };
    this.exportTableModalComponent.showModal(tableParams);
  }

  protected loadDefault() {
    this.currentSearch = '';
    this.load(1 , this.pageSize, null, null, [], []);
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    const { pageSize, sort } = params;
    // First emit from nz-table can be page 2 (empty keepState id / internal index). Always start at 1 (BAO-994).
    const pageIndex = this.lastLoadParams.pageIndex == null ? 1 : params.pageIndex;
    const currentSort = sort.find(item => item.value !== null);
    const sortField = (currentSort && currentSort.key) || null;
    const sortOrder = (currentSort && currentSort.value) || null;
    const filter = params.filter;

    const search = this.assignSearch(this.currentSearch);
    if (pageSize !== this.lastLoadParams.pageSize ||
      (pageIndex !== this.lastLoadParams.pageIndex && pageIndex > 0) ||
      sortField !== this.lastLoadParams.sortField ||
      sortOrder !== this.lastLoadParams.sortOrder ||
      (filter.length > 0  && this.lastLoadParams.filter.length > 0 && filter !== this.lastLoadParams.filter)
    ) {
      this.load(pageIndex, pageSize, sortField, sortOrder, filter, search, this.lastLoadParams.additionalFilters);
    }
  }

  load(
    pageIndex: number,
    pageSize: number,
    sortField: string | null,
    sortOrder: string | null,
    filter: { key: string; value: string[] }[],
    search: { key: string; value: string[] }[],
    additionalFilters: { key: string; value: string[] }[] = [],
  ): void {
    if (!sortField && !sortOrder && this.defaultSorting) {
      sortField = this.defaultSorting.sortField;
      sortOrder = this.defaultSorting.sortOrder;
    }

    this.lastLoadParams = {
      pageIndex,
      pageSize,
      sortField,
      sortOrder,
      filter,
      search,
      additionalFilters
    };
    this.pageSize = pageSize;
    this.updateTableScroll();
    filter = [...filter, ...this.predefindedFilters];
    this.persistTablePrefs();
    this.callService(pageIndex, pageSize, sortField, sortOrder, filter, search, additionalFilters);
  }

  callService( pageIndex: number,
               pageSize: number,
               sortField: string | null,
               sortOrder: string | null,
               filters: Array<{ key: string; value: string[]; operator?: FilterOperator; }>,
               search: Array<{ key: string; value: string[]; operator?: FilterOperator; }>,
               additionalFilters: Array<{ key: string; value: string[]; operator?: FilterOperator; }>,
  ) {
    this.loading = true;
    this.listError = false;
    this._cdr.detectChanges();
    this._listService.getQueryableAll(pageIndex, pageSize, sortField, sortOrder, filters, search, additionalFilters).subscribe(res => {
      this.loading = false;
      this.listError = false;
      this.data = res;
      this.onListDataAvailable();
      this._cdr.detectChanges();
    }, this.parseError);
  }

  public onListDataAvailable(): void {}

  parseError = data => {
    this.loading = false;
    this.listError = true;
    this._cdr.detectChanges();
    this._notifyService.pushError(undefined, this._errorResolver.getMessage(
      (data && data.InternalCode) ? data.InternalCode : '',
      data?.Message || data?.message
    ));
  }

  deleteElement = (id: number) => {
    this.loading = true;
    this._listService.delete(id.toString()).pipe(finalize(() => this.loading = false)).subscribe(() => {
      this.onRefreshList(false);
    }, this.parseError);
  }

  search = (value: string) => {
    this.currentSearch = value;
    if (this.searchColumns === null || this.searchColumns === undefined) {
      this.buildSearchColumns();
    }

    this.load(1,
      this.lastLoadParams.pageSize,
      null,
      null,
      [],
      this.assignSearch(value));
  }

  private buildSearchColumns() {
    this.searchColumns = (this.tableRef.nzData.length > 0 ? Object.keys(this.tableRef.nzData[0]) : []);
  }

  private assignSearch(value) {
    if (value.trim() === '') {
      return [];
    }
    return this.searchColumns.map((el) => ({ key: el, value: [value] }));
  }

  protected updateCheckedSet(id: number, checked: boolean): void {
    checked ? this.setOfCheckedId.add(id) : this.setOfCheckedId.delete(id);
  }

  protected refreshCheckedStatus(): void {
    this.checked = this.data.results.every((item: any) => this.setOfCheckedId.has(item.id));
    this.indeterminate = this.data.results.some((item: any) => this.setOfCheckedId.has(item.id)) && !this.checked;
  }

  public onItemChecked(id: number, checked: boolean): void {
    this.updateCheckedSet(id, checked);
    this.refreshCheckedStatus();
  }

  public onAllChecked(value: boolean): void {
    this.data.results.forEach((item: any) => this.updateCheckedSet(item.id, value));
    this.refreshCheckedStatus();
  }

  public ngOnDestroy(): void {
    this.elementRef.nativeElement.removeEventListener(COLUMN_LAYOUT_EVENT, this.onColumnLayout);
    this.tableWidthObserver?.disconnect();
    this.tableWidthObserver = null;
    if (this.tableWidthRaf) {
      cancelAnimationFrame(this.tableWidthRaf);
      this.tableWidthRaf = 0;
    }
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
